// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: cbfeb44a-9ef8-4b22-a766-5581e58e455e

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, FormControlLabel, Checkbox, CircularProgress, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';
import HelpBubble from './HelpBubble';

// User administration. Backed by Cognito through the API — the server re-checks
// every permission, so this UI is convenience, not the security boundary.
//   master (OptimumAdmins) -> manage users in any tenant
//   tenant admin           -> manage users in their own tenant only

const ROLES = ['admin', 'editor', 'viewer'];
// Who to call at this client. The rank is a fact about the WORKSPACE, not about
// the person, so it is fetched from /customers/contacts (Postgres) rather than
// read off the user row — see the long note on that route for why there is
// deliberately no copy of it in the directory.
const RANKS = [
  { value: '', label: '—' },
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
];
const BLANK = {
  email: '', firstName: '', lastName: '', tenantId: '', tenantName: '',
  roleId: 'viewer', isMaster: false, domainOverride: false, temporaryPassword: '',
};

export default function UserAdmin({ tenants = [] }) {
  const [users, setUsers] = useState([]);
  // tenantId -> { email -> rank }. Keyed by TENANT as well as address, never by
  // address alone: a master's list spans every workspace, and a key that is only
  // the email would merge two clients' rosters the moment two people shared one.
  const [contacts, setContacts] = useState({});
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [dialog, setDialog] = useState(null); // { mode:'create'|'edit', user }
  const [reset, setReset] = useState(null); // { user, password }
  const [busy, setBusy] = useState(false);
  // A password the admin now has to pass on. Held in its own persistent panel
  // rather than a toast: it is shown exactly once and there is no way to get it
  // back, so it must not disappear on the next action or a stray click.
  const [credential, setCredential] = useState(null); // { email, password, verb }

  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here, before the !res.ok check below could report the real
    // status, so a 502 surfaced to the user as "Unexpected token <".
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    // Any error envelope that is not an array used to reach users.map and
    // throw during render — a white screen instead of a message.
    try {
      const list = await api(`/users${showDeactivated ? '?includeDeleted=1' : ''}`);
      if (!Array.isArray(list)) throw new Error('The user service returned an unexpected response.');
      setUsers(list);
    }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }

    // Separate request, and a separate failure. Contacts are a nicety on this
    // screen; the user list is not. A contacts endpoint that is not deployed yet
    // must not take the roster down with it, which is why this is not awaited
    // inside the try above.
    try {
      const rows = await api('/customers/contacts');
      const byTenant = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        const t = r.tenant_id || r.tenantId || '';
        byTenant[t] = byTenant[t] || {};
        byTenant[t][String(r.user_email || r.userEmail || '').toLowerCase()] = r.contact_rank || r.contactRank || '';
      });
      setContacts(byTenant);
    } catch (e) { /* contacts unavailable — the column just renders blank */ }
  }, [api, showDeactivated]);

  useEffect(() => { load(); }, [load]);

  const rankOf = (u) => (contacts[u.tenantId] || {})[String(u.email).toLowerCase()] || '';

  const setRank = async (u, rank) => {
    setErr(''); setMsg('');
    try {
      if (!rank) {
        await api(`/customers/contacts?tenantId=${encodeURIComponent(u.tenantId)}&email=${encodeURIComponent(u.email)}`,
          { method: 'DELETE' });
        setMsg(`${u.email} is no longer a listed contact.`);
      } else {
        const r = await api('/customers/contacts', {
          method: 'PUT',
          body: JSON.stringify({
            tenantId: u.tenantId,
            email: u.email,
            rank,
            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
          }),
        });
        // The server demotes an incumbent primary in the same transaction as the
        // promotion, because the database allows only one. Say who moved rather
        // than silently reshuffling somebody's client roster.
        setMsg(r && r.demoted
          ? `${u.email} is now the primary contact. ${r.demoted} became a secondary.`
          : `${u.email} is now a ${rank} contact.`);
      }
      await load();
    } catch (e) { setErr(e.message); }
  };

  const submit = async () => {
    const u = dialog.user;
    setBusy(true); setErr(''); setMsg('');
    try {
      const tenant = tenants.find((t) => t.id === u.tenantId);
      const payload = { ...u, tenantName: tenant ? tenant.name : u.tenantName };
      if (dialog.mode === 'create') {
        await api('/users', { method: 'POST', body: JSON.stringify(payload) });
        setCredential({ email: u.email, password: u.temporaryPassword, verb: 'created' });
      } else {
        await api('/users', { method: 'PUT', body: JSON.stringify(payload) });
        if (u.resetPassword) setCredential({ email: u.email, password: u.resetPassword, verb: 'updated' });
        else setMsg(`Updated ${u.email}`);
      }
      setDialog(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // DEACTIVATE, NOT DELETE. The account is disabled and tombstoned; the record
  // stays so that every audit entry this person ever wrote still resolves to a
  // name, a workspace and a role. Removing the row would turn years of history
  // into an unresolvable address, which is the same reason a user's workspace
  // can never be changed.
  const remove = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}?\n\nThey lose access immediately. Their record is kept so the history they left behind still names them, and you can restore them.`)) return;
    setErr(''); setMsg('');
    try {
      await api(`/users?email=${encodeURIComponent(u.email)}&tenantId=${encodeURIComponent(u.tenantId)}`, { method: 'DELETE' });
      setMsg(`Deactivated ${u.email}`);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const restore = async (u) => {
    setErr(''); setMsg('');
    try {
      await api('/users/restore', { method: 'POST', body: JSON.stringify({ email: u.email }) });
      setMsg(`Restored ${u.email}. They can sign in again with their existing password.`);
      await load();
    } catch (e) { setErr(e.message); }
  };

  // Math.random is not a source of secrets. These strings are handed to a person
  // and used once to reach the set-a-real-password screen, but a guessable temp
  // password is still a way into an account, so draw from the CSPRNG.
  const genPassword = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const bytes = new Uint32Array(14);
    window.crypto.getRandomValues(bytes);
    // Guarantees the pool policy (upper, lower, digit) regardless of the draw.
    return `${Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')}7Aa`;
  };

  // Reset only. Deliberately sends the narrowest possible payload — email to
  // identify, tenantId because the server checks it before allowing the change,
  // and nothing else. Sending the whole user object here would let a stale row
  // in this table silently overwrite a role or tenant somebody changed since
  // the list was loaded.
  const submitReset = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api('/users', {
        method: 'PUT',
        body: JSON.stringify({
          email: reset.user.email,
          tenantId: reset.user.tenantId,
          resetPassword: reset.password,
        }),
      });
      setCredential({ email: reset.user.email, password: reset.password, verb: 'reset' });
      setReset(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const setU = (patch) => setDialog((d) => ({ ...d, user: { ...d.user, ...patch } }));

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Users</Typography>
        <HelpBubble topic="userAdmin" />
        <Chip size="small" label={iAmMaster ? 'Optimum master — all tenants' : `${me && me.tenantName ? me.tenantName : 'your tenant'} only`} />
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Checkbox size="small" checked={showDeactivated} onChange={(e) => setShowDeactivated(e.target.checked)} />}
          label={<span style={{ fontSize: 13 }}>Show deactivated</span>} />
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Button startIcon={<AddIcon />} variant="contained" sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}
          onClick={() => setDialog({ mode: 'create', user: { ...BLANK, tenantId: iAmMaster ? '' : (me && me.tenantId) || '', temporaryPassword: genPassword() } })}>
          New user
        </Button>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {credential && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCredential(null)}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>
            {credential.verb === 'created' ? 'User created' : 'Password reset'}
            {' — '}
            {credential.email}
          </Typography>
          <Typography sx={{ fontSize: 13, mb: 1 }}>
            Give them this temporary password. They will be asked to set a permanent one, and then
            to set up an authenticator app, the first time they sign in.
          </Typography>
          <Box sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 16, fontWeight: 700, letterSpacing: 1, userSelect: 'all',
            p: 1, bgcolor: '#fff', border: '1px solid #C8E6C9', borderRadius: 1, mb: 1,
          }}>
            {credential.password}
          </Box>
          <Typography sx={{ fontSize: 12 }}>
            <b>No email was sent.</b>
            {' '}
            Pass it on yourself — this is shown once and cannot be retrieved afterwards.
          </Typography>
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => {
                const off = !!u.deletedAt || u.enabled === false;
                return (
                  <TableRow key={u.username} hover sx={off ? { opacity: 0.55 } : undefined}>
                    <TableCell sx={{ fontWeight: 600 }}>{u.email}</TableCell>
                    <TableCell>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                    <TableCell>
                      {u.isMaster
                        ? <Chip size="small" label="ALL TENANTS" sx={{ bgcolor: '#021F4B', color: '#fff', height: 20 }} />
                        : (u.tenantName || u.tenantId || '—')}
                    </TableCell>
                    <TableCell><Chip size="small" label={u.roleId} variant="outlined" sx={{ height: 20 }} /></TableCell>
                    {/* Being a contact grants nothing — no screen, record or token
                        reads it. It is a phone-book entry, which is why it sits
                        beside the role rather than inside it. */}
                    <TableCell>
                      <TextField select size="small" variant="standard" value={rankOf(u)}
                        disabled={off || !u.tenantId}
                        onChange={(e) => setRank(u, e.target.value)}
                        sx={{ minWidth: 104, '& .MuiInput-input': { fontSize: 13 } }}>
                        {RANKS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {off
                        ? <Chip size="small" label="deactivated" sx={{ height: 20 }} />
                        : (
                          <Chip size="small" label={u.status === 'FORCE_CHANGE_PASSWORD' ? 'invite pending' : (u.status || '').toLowerCase()}
                            color={u.status === 'CONFIRMED' ? 'success' : 'default'} sx={{ height: 20 }} />
                        )}
                    </TableCell>
                    <TableCell align="right">
                      {off ? (
                        <Tooltip title="Restore access">
                          <IconButton size="small" onClick={() => restore(u)}><RestoreIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Reset password">
                            <IconButton size="small" onClick={() => setReset({ user: u, password: genPassword() })}>
                              <KeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit / assign">
                            <IconButton size="small" onClick={() => setDialog({ mode: 'edit', user: { ...u } })}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Deactivate — they lose access, their history keeps their name">
                            <IconButton size="small" onClick={() => remove(u)}><BlockIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!users.length && (
                <TableRow><TableCell colSpan={7} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>No users yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
          {dialog && dialog.mode === 'create' ? 'Create user' : 'Edit user'}
        </DialogTitle>
        {dialog && (
          <DialogContent dividers>
            <TextField fullWidth size="small" label="Email" sx={{ mb: 2 }} value={dialog.user.email}
              disabled={dialog.mode === 'edit'} onChange={(e) => setU({ email: e.target.value })} />
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="First name" value={dialog.user.firstName || ''} onChange={(e) => setU({ firstName: e.target.value })} />
              <TextField fullWidth size="small" label="Last name" value={dialog.user.lastName || ''} onChange={(e) => setU({ lastName: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField select fullWidth size="small" label="Tenant" value={dialog.user.tenantId || ''}
                disabled={!iAmMaster} onChange={(e) => setU({ tenantId: e.target.value })}>
                {tenants.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <TextField select fullWidth size="small" label="Role" value={dialog.user.roleId || 'viewer'}
                onChange={(e) => setU({ roleId: e.target.value })}>
                {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Stack>

            {iAmMaster && (
              <FormControlLabel sx={{ display: 'block' }}
                control={<Checkbox size="small" checked={!!dialog.user.isMaster} onChange={(e) => setU({ isMaster: e.target.checked })} />}
                label={<span style={{ fontSize: 13 }}>Optimum master — can see and manage <b>all</b> client tenants</span>} />
            )}
            <FormControlLabel sx={{ display: 'block' }}
              control={<Checkbox size="small" checked={!!dialog.user.domainOverride} onChange={(e) => setU({ domainOverride: e.target.checked })} />}
              label={<span style={{ fontSize: 13 }}>Email-domain override (allow an address outside the client's domain)</span>} />

            {dialog.mode === 'create' ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <TextField fullWidth size="small" label="Temporary password" value={dialog.user.temporaryPassword}
                  onChange={(e) => setU({ temporaryPassword: e.target.value })}
                  helperText="They will be asked to set a permanent password at first sign-in." />
                <Button size="small" startIcon={<KeyIcon />} onClick={() => setU({ temporaryPassword: genPassword() })}>New</Button>
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: '#5C6B7A', mt: 2 }}>
                To give this person a new password, close this and use the key icon on their row.
                Resetting is kept separate so it cannot ride along with an attribute change.
              </Typography>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={busy || !dialog || !dialog.user.email}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Saving…' : (dialog && dialog.mode === 'create' ? 'Create user' : 'Save changes')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!reset} onClose={() => !busy && setReset(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>Reset password</DialogTitle>
        {reset && (
          <DialogContent dividers>
            <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
              This replaces the password for
              {' '}
              <b>{reset.user.email}</b>
              {' '}
              immediately. It is temporary: they will be asked to set a permanent one at their next
              sign-in. Anyone signed in as them stays signed in until their session expires.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField fullWidth size="small" label="Temporary password" value={reset.password}
                onChange={(e) => setReset((r) => ({ ...r, password: e.target.value }))}
                helperText="8+ characters, with upper, lower, and a number." />
              <Button size="small" startIcon={<KeyIcon />} sx={{ mt: 0.5 }}
                onClick={() => setReset((r) => ({ ...r, password: genPassword() }))}>New</Button>
            </Stack>
            <Alert severity="info" sx={{ mt: 2, fontSize: 12.5 }}>
              No email is sent. You will be shown the password once, to pass on yourself.
            </Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setReset(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitReset} disabled={busy || !reset || !reset.password}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Resetting…' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
